/**
 * Language switcher component.
 */

import { useTranslation } from 'react-i18next';
import { Dropdown } from 'react-bootstrap';

// RTL language codes
const RTL_LANGUAGES = ['ar', 'fa', 'he', 'ku'];

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    const languageCode = lng.split('-')[0];
    const isRTL = RTL_LANGUAGES.includes(languageCode);

    // Update HTML lang and dir attributes
    document.documentElement.lang = languageCode;
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.body.dir = isRTL ? 'rtl' : 'ltr';

    // Add/remove RTL class for CSS styling
    if (isRTL) {
      document.documentElement.classList.add('rtl');
      document.body.classList.add('rtl');
    } else {
      document.documentElement.classList.remove('rtl');
      document.body.classList.remove('rtl');
    }
  };

  const getLanguageName = (code: string): string => {
    const names: Record<string, string> = {
      en: 'English',
      fr: 'Français',
      de: 'Deutsch',
      it: 'Italiano',
      es: 'Español',
      nl: 'Nederlands',
      pl: 'Polski',
      sv: 'Svenska',
      da: 'Dansk',
      fi: 'Suomi',
      no: 'Norsk',
      pt: 'Português',
      el: 'Ελληνικά',
      cs: 'Čeština',
      sr: 'Српски',
      tr: 'Türkçe',
      fa: 'فارسی',
      ar: 'العربية',
      ku: 'کوردی',
      he: 'עברית',
      ja: '日本語',
      ko: '한국어',
      zh: '中文',
      hi: 'हिन्दी',
      ru: 'Русский',
      uk: 'Українська',
    };
    return names[code] || code.toUpperCase();
  };

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'de', name: 'Deutsch' },
    { code: 'it', name: 'Italiano' },
    { code: 'es', name: 'Español' },
    { code: 'nl', name: 'Nederlands' },
    { code: 'pl', name: 'Polski' },
    { code: 'sv', name: 'Svenska' },
    { code: 'da', name: 'Dansk' },
    { code: 'fi', name: 'Suomi' },
    { code: 'no', name: 'Norsk' },
    { code: 'pt', name: 'Português' },
    { code: 'el', name: 'Ελληνικά' },
    { code: 'cs', name: 'Čeština' },
    { code: 'sr', name: 'Српски' },
    { code: 'tr', name: 'Türkçe' },
    { code: 'fa', name: 'فارسی' },
    { code: 'ar', name: 'العربية' },
    { code: 'ku', name: 'کوردی' },
    { code: 'he', name: 'עברית' },
    { code: 'ja', name: '日本語' },
    { code: 'ko', name: '한국어' },
    { code: 'zh', name: '中文' },
    { code: 'hi', name: 'हिन्दी' },
    { code: 'ru', name: 'Русский' },
    { code: 'uk', name: 'Українська' },
  ];

  const currentLanguage = i18n.language.split('-')[0] || 'en';

  return (
    <Dropdown>
      <Dropdown.Toggle
        variant="outline-secondary"
        id="language-dropdown"
        style={{
          fontSize: '0.875rem',
          padding: '0.375rem 0.75rem',
          border: '1px solid var(--myant-border-color)',
          backgroundColor: 'transparent',
        }}
      >
        <i className="fas fa-globe me-2"></i>
        {getLanguageName(currentLanguage)}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {languages.map((lang) => (
          <Dropdown.Item
            key={lang.code}
            active={currentLanguage === lang.code}
            onClick={() => changeLanguage(lang.code)}
          >
            {lang.name}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}
